import { useEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigation } from './i18n';
import { useSignalRStatus, SignalRStatus } from '../../hooks/useSignalRStatus';

// The store reconnects forever, so a dropped connection stays `reconnecting` indefinitely.
// Treat reconnecting for longer than this as actually down (red).
const RECONNECTING_RED_AFTER_MS = 10000;

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return `${minutes} min ${seconds} sec`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return `${hours} hr ${remMinutes} min`;
};

export const SignalRStatusIndicator = () => {
  const status = useSignalRStatus();
  const { palette } = useTheme();
  const { t } = useTranslation(Navigation);

  // Timestamp of when the connection first left `connected`; null while connected/idle.
  const downSinceRef = useRef<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (status === 'connected' || status === 'idle') {
      downSinceRef.current = null;
      return;
    }
    // reconnecting / disconnected: start the clock and tick every second so the duration updates.
    downSinceRef.current ??= Date.now();
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  const downMs = downSinceRef.current === null ? 0 : now - downSinceRef.current;

  // A reconnect that lasts past the threshold is shown as down (red).
  const effectiveStatus: SignalRStatus =
    status === 'reconnecting' && downMs > RECONNECTING_RED_AFTER_MS ? 'disconnected' : status;

  const config: Record<SignalRStatus, { color: string; label: string }> = {
    connected: { color: palette.success.main, label: t('connection.connected') },
    reconnecting: { color: palette.warning.main, label: t('connection.reconnecting') },
    disconnected: { color: palette.error.main, label: t('connection.disconnected') },
    idle: { color: palette.action.disabled, label: t('connection.idle') },
  };

  const { color, label } = config[effectiveStatus];
  // Only show the elapsed time once we consider the link down (red), not during the yellow grace window.
  const duration =
    effectiveStatus === 'disconnected' && downSinceRef.current !== null
      ? formatDuration(downMs)
      : null;

  return (
    <Tooltip title={duration ? `${label} (${duration})` : label} arrow>
      {/* Fixed-width, right-aligned area so the dot stays put and the duration grows
          leftward into reserved space — the header never reflows. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '6px',
          width: 120,
          flexShrink: 0,
        }}
      >
        {duration && (
          <Typography
            sx={{
              fontSize: '12px',
              color,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {duration}
          </Typography>
        )}
        <Box
          role="status"
          aria-label={label}
          sx={{
            width: 15,
            height: 15,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
            // Static ring to make the "unstable" state stand out beyond colour alone.
            ...(effectiveStatus === 'reconnecting' && { boxShadow: `0 0 0 3px ${alpha(color, 0.3)}` }),
          }}
        />
      </Box>
    </Tooltip>
  );
};
